import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import type { RootState } from "../store";
import Theme from "../utils/theme";
import { useSelector } from "react-redux";
import type { IImage } from "../types/image";
import React, { useMemo } from "react";
import { useSwipeNavigation } from "../hooks/useSwipeNavigation";

// *** Component specific types ***
interface IImageStackProps {
    images: IImage[];
    currentImageIndex: number;
    setCurrentImageIndex: React.Dispatch<React.SetStateAction<number>>;
    setOpenImageModal: React.Dispatch<React.SetStateAction<boolean>>;
    showCaption?: boolean;
}

// ***** Main Component ***** //
const ImageStack: React.FC<IImageStackProps> = ({
    images,
    currentImageIndex,
    setCurrentImageIndex,
    setOpenImageModal,
    showCaption = false,
}) => {
    // *** Root States *** //
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const { handleTouchStart, handleTouchMove, handleTouchEnd } = useSwipeNavigation({
        currentIndex: currentImageIndex,
        maxIndex: images.length - 1,
        onChange: setCurrentImageIndex,
    });

    return (
        <>
            <Box
                sx={{
                    position: "relative",
                    maxWidth: { xs: 240, sm: 360, md: 480 },
                    width: "100%",
                    height: { xs: 240, sm: 360, md: 480 },
                    mx: "auto",
                    mb: 1,
                    overflow: "visible",
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {images.map((img, i) => {
                    const offset = i - currentImageIndex;
                    let style: React.CSSProperties = {};

                    if (offset === 0) {
                        style = {
                            zIndex: 3,
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%) scale(1)",
                            boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                        };
                    } else if (Math.abs(offset) === 1) {
                        style = {
                            zIndex: 2,
                            left: offset < 0 ? "30%" : "70%",
                            top: "50%",
                            transform: "translate(-50%, -50%) scale(0.85)",
                            opacity: 0.7,
                            filter: "blur(1px)",
                        };
                    } else {
                        style = { display: "none" };
                    }

                    return (
                        <Box
                            key={i}
                            component="img"
                            src={img.url}
                            alt={`Post image ${i + 1}`}
                            sx={{
                                position: "absolute",
                                width: { xs: 240, sm: 360, md: 480 },
                                height: { xs: 240, sm: 360, md: 480 },
                                objectFit: "cover",
                                borderRadius: 3,
                                transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
                                cursor: offset === 0 ? "pointer" : "default",
                                ...style,
                            }}
                            onClick={() => offset === 0 && setOpenImageModal(true)}
                        />
                    );
                })}
                {/* Navigation Arrows */}
                {currentImageIndex > 0 && (
                    <IconButton
                        onClick={() => setCurrentImageIndex(currentImageIndex - 1)}
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 4,
                            background: "rgba(255,255,255,0.7)",
                            "&:hover": {
                                background: "rgba(255,255,255,0.7)",
                            },
                        }}
                    >
                        <ArrowBackIosNew sx={{ color: theme.palette.primary.main }} />
                    </IconButton>
                )}
                {currentImageIndex < images.length - 1 && (
                    <IconButton
                        onClick={() => setCurrentImageIndex(currentImageIndex + 1)}
                        sx={{
                            position: "absolute",
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 4,
                            background: "rgba(255,255,255,0.7)",
                            "&:hover": {
                                background: "rgba(255,255,255,0.7)",
                            },
                        }}
                    >
                        <ArrowForwardIos sx={{ color: theme.palette.primary.main }} />
                    </IconButton>
                )}
            </Box>

            {showCaption && (
                <Box
                    sx={{ mb: 2, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                    onClick={() => setOpenImageModal(true)}
                >
                    <Typography variant="subtitle2" sx={{ textAlign: "center" }}>
                        {images[currentImageIndex]?.caption}
                    </Typography>
                </Box>
            )}
        </>
    );
};

export default ImageStack;
